import type { ProfileListEntry } from "@/components/jobProfiles/types";
import type { JobProfileStatus, ProfileChannel, ProfileOwner } from "@/types/profile";
import { PUBLISHING } from "@/constants/commonConstants";
import { DEFAULT_PROFILE_DASHBOARD_PLC } from "@/constants/profileConstants";
import { normalizeProfilePlatformValues } from "@/utilities/profiles/profileFormUtils";

/** Publishing types that don't use a job profile: the Job Profile step has no Channel, Job Profile, or Channel Tag. */
const PROFILE_LESS_PUBLISHING_TYPES = [PUBLISHING.BADGE_ICON, PUBLISHING.FILE];

export const isJobProfileNotApplicable = (publishing: string | undefined): boolean =>
  PROFILE_LESS_PUBLISHING_TYPES.includes(String(publishing || ""));

/** Supported top-level profile channels (API / YAML `Channels[].Name`, lowercase). */
export const PARENT_CHANNEL_NAMES = ["release", "development", "insider"] as const;

export type ParentChannelName = (typeof PARENT_CHANNEL_NAMES)[number];

export const DEVELOPMENT_CHANNEL_NAME: ParentChannelName = "development";

/** Channel whose config is shown for the `_DEFAULT` profile in submit-job (nothing is selectable there). */
export const RELEASE_CHANNEL_NAME: ParentChannelName = "release";

export const isDevelopmentChannel = (channel: string | undefined): boolean =>
  channel?.trim().toLowerCase() === DEVELOPMENT_CHANNEL_NAME;

export const isSupportedParentChannel = (name: string | undefined): boolean => {
  const key = name?.trim().toLowerCase();
  return !!key && (PARENT_CHANNEL_NAMES as readonly string[]).includes(key);
};

/** Keeps only supported profile channels; drops legacy lanes (staging, feature, etc.). */
export const filterSupportedProfileChannels = (channels: ProfileChannel[] | undefined): ProfileChannel[] =>
  (channels ?? []).filter((channel) => isSupportedParentChannel(channel.Name));

const parentChannelSortIndex = (name: string): number => {
  const index = PARENT_CHANNEL_NAMES.indexOf(name.toLowerCase() as ParentChannelName);
  return index >= 0 ? index : PARENT_CHANNEL_NAMES.length;
};

/** Parent channels present in the profile, in canonical order. */
export const getParentChannels = (channels: ProfileChannel[] | undefined): ProfileChannel[] =>
  filterSupportedProfileChannels(channels).sort(
    (a, b) => parentChannelSortIndex(a.Name) - parentChannelSortIndex(b.Name)
  );

/** `"release"` -> `"Release"` for display; stored values stay lowercase API names. */
export const formatChannelLabel = (name: string | undefined): string => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/** Single channel value sent as `JobInfo.Channel` (no sub-channels). */
export const toApiChannel = (channel: string | undefined, _subChannel?: string): string => channel?.trim() ?? "";

/**
 * Profile/channel/tag fields for a profile named outside the picker (uploaded YAML, republished job).
 * A supported parent channel is used directly (any casing); anything else waits in `pendingChannel` until the
 * profile loads (see resolveChannelSelection). Without a profile, channel and tag are dropped.
 */
export const getStoredProfileFields = (
  profileName: string | undefined,
  apiChannel: string | undefined,
  channelTag: string | undefined
): { parentJobProfile: string; channel: string; subChannel: string; pendingChannel: string; tag: string } => {
  const profile = String(profileName ?? "").trim();
  const rawChannel = String(apiChannel ?? "").trim();
  const normalizedChannel = rawChannel.toLowerCase();
  const channel = profile && isSupportedParentChannel(normalizedChannel) ? normalizedChannel : "";
  return {
    parentJobProfile: profile,
    channel,
    subChannel: "",
    pendingChannel: profile && rawChannel && !channel ? rawChannel : "",
    tag: profile ? String(channelTag ?? "").trim() : ""
  };
};

/** Maps stored `JobInfo.Channel` to the submit-job channel field. */
export const resolveChannelSelection = (
  channels: ProfileChannel[] | undefined,
  apiChannel: string | undefined
): { channel: string; subChannel: string } => {
  if (!apiChannel) return { channel: "", subChannel: "" };
  const match = getParentChannels(channels).find((entry) => entry.Name === apiChannel);
  return match ? { channel: match.Name, subChannel: "" } : { channel: "", subChannel: "" };
};

/** Rows from the `_DEFAULT` item (PLC) — returned by the list API when the item has no profiles of its own. */
export const isDefaultProfileEntry = (entry: ProfileListEntry): boolean =>
  entry.plc.trim().toUpperCase() === DEFAULT_PROFILE_DASHBOARD_PLC;

/** True when the list has profiles but all are `_DEFAULT`, so submit-job lets the user skip Channel / Job Profile / Channel Tag. */
export const hasOnlyDefaultProfiles = (entries: ProfileListEntry[]): boolean =>
  entries.length > 0 && entries.every(isDefaultProfileEntry);

/** Maps submit-job platform (`windows`/`mac`/`linux`) to profile list API `platform` query values. */
export const toProfileListApiPlatform = (platform: string | undefined): string | undefined => {
  const [canonical] = normalizeProfilePlatformValues([String(platform || "")]);
  return canonical || undefined;
};

/** Stable key for “list fetch matches current Item + platform + publishing type”. */
export const buildProfileListRequestKey = (plc: string, platform: string, releaseType: string): string => {
  const item = plc?.trim();
  const apiPlatform = toProfileListApiPlatform(platform);
  const publishingType = releaseType?.trim();
  if (!item || !apiPlatform || !publishingType) {
    return "";
  }
  return `${item}|${apiPlatform}|${publishingType}`;
};

/**
 * Channels (parent channels, lowercase) available for the selected profile: the channels on its list-API row,
 * else the loaded profile's channels (a YAML/republished profile has no list row), else every channel.
 */
export const getAvailableRingNames = (entry?: ProfileListEntry, channels?: ProfileChannel[]): string[] => {
  const fromEntry = (entry?.channels ?? []).filter((name) => isSupportedParentChannel(name)).map((name) => name.toLowerCase());
  if (fromEntry.length) return fromEntry;
  const fromProfile = getParentChannels(channels).map((channel) => channel.Name.toLowerCase());
  if (fromProfile.length) return fromProfile;
  return [...PARENT_CHANNEL_NAMES];
};

export const profileNamesFromFilteredEntries = (entries: ProfileListEntry[]): string[] =>
  entries.map((entry) => entry.profileName);

/** Dashboard Publisher column — first registry owner display name, with sensible fallbacks. */
export const getProfilePublisherColumnValue = (entry: ProfileListEntry): string => {
  const firstOwner: ProfileOwner | undefined = entry.Owners?.[0];
  const ownerName = firstOwner?.Name?.trim();
  if (ownerName) return ownerName;
  const ownerEmail = firstOwner?.Email?.trim();
  if (ownerEmail) return ownerEmail;
  const publisher = entry.publisher?.trim();
  if (publisher) return publisher;

  return "—";
};

/** Applies dashboard status filter; `status` omitted or empty means all statuses. */
export const filterProfileEntriesByStatus = (
  entries: ProfileListEntry[],
  status?: JobProfileStatus | ""
): ProfileListEntry[] => {
  if (!status) return entries;
  return entries.filter((entry) => entry.status === status);
};

/** True when Item Info step can proceed: Item required; profile/channel optional as a pair. */
export const isItemInfoStepValid = (
  plc: string,
  parentJobProfile: string,
  channel: string,
  _subChannel: string,
  channels?: ProfileChannel[]
): boolean => {
  if (!plc) return false;
  if (!parentJobProfile) return true;
  const apiChannel = toApiChannel(channel);
  if (!apiChannel) return false;
  return getParentChannels(channels).some((entry) => entry.Name === apiChannel);
};
